/* eslint-disable indent */
import { Controller, useFormContext } from "react-hook-form"
import { useSelector } from "react-redux"
import { TiposAlojamientos } from "src/gql/schema"
import InputTabs from "src/shared/components/forms/input-tabs/InputTabs"
import Dropdown from "src/shared/components/forms/selelct-dropdown/Dropdown"
import Counter from "src/shared/components/forms/counter/Counter"
import InputDate from "src/shared/components/forms/input-date/InputDate"
import Switch from "src/shared/components/forms/switch/Switch"
import { useDate } from "src/shared/hooks/useDate"
import { formatCurrency } from "src/shared/hooks/formatCurrency"
import { addDays } from "src/shared/helpers/addDays"
import { substractDays } from "src/shared/helpers/substractDays"
import { getCurrencyFormat } from "src/utils/string"
import { selectTarifa } from "src/store/ventaHabitacion/ventaHabitacionSlice"
import { RootState } from "src/store/store"
import { useTarifasValidation } from "src/pages/reservaciones/registro-reservas/hooks/useTarifasValidation"
import FormSection from "../../components/FormSection/FormSection"
import FormSectionRow from "../../components/FormSectionRow/FormSectionRow"
import { DefaultValuesType } from "../../VentaHabitacion.types"

interface HospedajeProps {
    // La reserva fija el tipo, la tarifa, las fechas y las horas extra
    isFromReserva: boolean
    disabled: boolean
    scheduleTypes: any
    validarTarifaPorFecha: boolean
    tipoBloqueado: boolean
    hospedajesExtraBloqueados: boolean
    earlyCheckInBloqueado: boolean
    isBetween12AMAndCheckInHour: boolean
    isBetween12AMAndCheckOutHour: boolean
}

// Tipo de hospedaje, tarifa, fechas, horas/hospedajes extra y check-in anticipado
const Hospedaje = ({
    isFromReserva,
    disabled,
    scheduleTypes,
    validarTarifaPorFecha,
    tipoBloqueado,
    hospedajesExtraBloqueados,
    earlyCheckInBloqueado,
    isBetween12AMAndCheckInHour,
    isBetween12AMAndCheckOutHour,
}: HospedajeProps) => {
    const { control, watch } = useFormContext<DefaultValuesType>()
    const { areSameDay, setHHMMSS } = useDate()
    const { validationByDate } = useTarifasValidation()
    const { tarifas, tarifaSeleccionada } = useSelector((root: RootState) => root.ventaHabitacion)

    const tipoHospedaje = watch("type")
    const dateRange = watch("date")

    return (
        <FormSection title="">
            <FormSectionRow>
                <Controller
                    control={control}
                    name={"type"}
                    rules={{ required: true }}
                    render={({ field: { value, onChange } }) => (
                        <InputTabs
                            disabled={disabled || tipoBloqueado}
                            containerClassName="venta-habitacion-input"
                            label="Tipo"
                            items={scheduleTypes}
                            value={value}
                            onChange={(e) => (isFromReserva ? null : onChange(e))}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name={"amount"}
                    rules={{ required: true }}
                    render={({ field: { value, onChange }, formState: { errors } }) => (
                        <div className="venta-habitacion-input">
                            <Dropdown
                                disabled={isFromReserva}
                                icon={"dollarCircle"}
                                iconInOptions={false}
                                errorHintText={errors.amount ? "Selecciona una opción" : ""}
                                label={"Tipo de tarifa"}
                                placeholder={"Selecciona una tarifa"}
                                options={tarifas
                                    .filter(
                                        (t) =>
                                            t.tipo_alojamiento === tipoHospedaje &&
                                            // en algunos casos la tarifa ya está fijada y no depende de la fecha
                                            (!validarTarifaPorFecha || validationByDate(t, dateRange?.[0]))
                                    )
                                    .map((t) => ({
                                        label: `${t.nombre} - ${getCurrencyFormat(t?.costo_habitacion)}`,
                                        value: t.tarifa_id,
                                    }))}
                                value={value}
                                onClick={({ value }) => {
                                    onChange(value)
                                    selectTarifa(tarifas?.find?.((t) => t.tarifa_id === value))
                                }}
                            />
                        </div>
                    )}
                />
                {tipoHospedaje === "hotel" && (
                    <Controller
                        name="date"
                        control={control}
                        rules={{
                            required: true,
                            validate: (v) => v.length > 1,
                        }}
                        // onChange del Controller le da el nuevo valor al input dentro del estado del formulario y le establece también el valor a este DatePicker
                        render={({ field: { onChange: onFieldChange, value: fieldValue }, formState: { errors } }) => (
                            <InputDate
                                modalClosableOnClickOutside
                                disabled={isFromReserva}
                                selectableOnDblClick={isBetween12AMAndCheckOutHour}
                                placeholder="Selecciona una fecha"
                                label="Fecha de entrada y salida"
                                inputClassName="venta-habitacion-input"
                                isRange={true}
                                onReset={() => {
                                    onFieldChange([
                                        setHHMMSS({
                                            startDate: new Date(),
                                            newHour: "00:00:00",
                                            isNewHourInUTC: false,
                                        }),
                                    ])
                                }}
                                disabledBeforeOrEqualDate={substractDays({
                                    date: new Date(),
                                    days: 1,
                                })}
                                disabledAfterOrEqualDate={
                                    !fieldValue.length
                                        ? new Date()
                                        : fieldValue.length === 2 && fieldValue[1] === fieldValue[2]
                                        ? fieldValue[1]
                                        : fieldValue.length === 2
                                        ? addDays({ date: fieldValue[1], days: 1 })
                                        : undefined
                                }
                                onChangeDblClick={(date) => {
                                    onFieldChange([date, date])
                                }}
                                onChange={(date) => {
                                    if (fieldValue.length === 0) {
                                        onFieldChange([date])
                                        return
                                    }
                                    if (date <= fieldValue[0]) {
                                        onFieldChange([date])
                                        return
                                    }
                                    if (fieldValue?.length === 2 && areSameDay(new Date(), date)) {
                                        onFieldChange([date])
                                        return
                                    }
                                    if (fieldValue?.length === 2) {
                                        onFieldChange([])
                                        return
                                    }
                                    onFieldChange([fieldValue[0], date])
                                }}
                                value={fieldValue}
                                errorHintText={errors.date ? "Selecciona un rango de fechas" : ""}
                            />
                        )}
                    />
                )}

                {tipoHospedaje === "motel" && (
                    <Controller
                        control={control}
                        name={"extraHospedajes"}
                        rules={{ required: false, min: 0 }}
                        render={({ field: { value, onChange } }) => (
                            <div className="venta-h-screen__count" style={{ columnGap: 20 }}>
                                <p className="venta-h-screen__count__label">
                                    Hospedaje extra
                                    {tarifaSeleccionada !== null ? (
                                        <span>{`(${getCurrencyFormat(
                                            tarifaSeleccionada?.costo_hospedaje_extra
                                        )} c/u)`}</span>
                                    ) : null}
                                </p>
                                <Counter
                                    stepCount={1}
                                    title="Horas extra"
                                    className="venta-h-screen__counter"
                                    max={undefined}
                                    disabled={false}
                                    min={0}
                                    value={value}
                                    onClick={onChange}
                                    disable={hospedajesExtraBloqueados}
                                />
                            </div>
                        )}
                    />
                )}
            </FormSectionRow>
            <FormSectionRow>
                {tarifaSeleccionada?.costo_hora_extra !== undefined && tarifaSeleccionada?.costo_hora_extra > 0 && (
                    <Controller
                        control={control}
                        name={"extraHours"}
                        rules={{ required: false, min: 0 }}
                        render={({ field: { value, onChange } }) => (
                            <div className="venta-h-screen__count" style={{ columnGap: 10, marginTop: 10 }}>
                                <p className="venta-h-screen__count__label">
                                    Horas extra
                                    {tarifaSeleccionada !== null ? (
                                        <span>{`(${getCurrencyFormat(
                                            tarifaSeleccionada?.costo_hora_extra
                                        )} c/u)`}</span>
                                    ) : null}
                                </p>
                                <Counter
                                    stepCount={2}
                                    title="Horas extra"
                                    className="venta-h-screen__counter"
                                    max={2}
                                    min={0}
                                    disabled={false}
                                    value={value}
                                    onClick={onChange}
                                    disable={isFromReserva}
                                />
                            </div>
                        )}
                    />
                )}
                {tipoHospedaje === TiposAlojamientos.Hotel && (
                    <Controller
                        control={control}
                        name={"earlyCheckIn"}
                        render={({ field: { value, onChange } }) => (
                            <div
                                className="venta-h-screen__early"
                                style={{
                                    marginTop:
                                        tarifaSeleccionada?.costo_hora_extra !== undefined &&
                                        tarifaSeleccionada?.costo_hora_extra > 0
                                            ? 30
                                            : 10,
                                }}
                            >
                                <Switch
                                    value={value}
                                    disabled={
                                        earlyCheckInBloqueado ||
                                        !!(
                                            // si no hay costo_early_checkin deshabilitar
                                            (
                                                !tarifaSeleccionada?.costo_early_checkin ||
                                                // se corrige deshabilitar el switch de checkin anticipado después de la hora del checkin
                                                !isBetween12AMAndCheckInHour ||
                                                // Si no hay fecha 1 deshabilitar
                                                !dateRange[0] ||
                                                // Si no hay fecha 2 deshabilitar
                                                !dateRange[1]
                                            )
                                            //|| areSameDay(dateRange[0], dateRange[1])
                                        )
                                    }
                                    label={
                                        <div>
                                            <span>Check-in anticipado </span>
                                            {tarifaSeleccionada?.costo_early_checkin ? (
                                                <span className="venta-h-screen__check-anticipado__label">
                                                    +{formatCurrency(tarifaSeleccionada.costo_early_checkin)}
                                                </span>
                                            ) : (
                                                <></>
                                            )}
                                        </div>
                                    }
                                    onChange={onChange}
                                />
                            </div>
                        )}
                    />
                )}
            </FormSectionRow>
        </FormSection>
    )
}

export default Hospedaje
